import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { sendEvent } from 'utils/Event';
import Box from '@mui/material/Box';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import Typography from '@mui/material/Typography';
import { CustomSortableHeader } from 'utils/Datagrid/CustomGenericDataGridUtils';
import {
  CustomHeaderWithoutSort,
  datagridMappingTheme,
} from 'utils/Datagrid/CustomCultureDataGridUtils';
import {
  editGroundTypeMappingData,
  getGroundTypeMappingDataByTypeAndStructure,
} from 'services/API/Cooperative';
import { IMPORT_TYPE } from 'utils/GlobalVariables';
import { CustomToolBarMappingDatagrid } from 'utils/Datagrid/CustomToolBarMappingDatagrid';
import useDataGridMappingActions from 'utils/Datagrid/useDataGridMappingActions';
import CustomCultureDataGrid from 'utils/Datagrid/CustomCultureDataGrid';
import { getOnlyAttributeUpdatedBetweenObjects } from 'utils/tools_functions';
import ImportMappingFileModal from './ImportMappingFileModal';
import AddGroundTypeMappingDataModal from './AddGroundTypeMappingDataModal';
import { TextField } from '@mui/material';
import AccordionErrorMappingImport from './AccordionErrorMappingImport';
import { MappingImportResponseContext } from 'utils/context';

const MappingGroundType = ({ groundTypeList, cooperativeId, mappingTypes }) => {
  const { t } = useTranslation();
  const {
    allMappingData,
    setAllMappingData,
    groundTypeMappingData,
    groundTypeMappingImportResponse,
  } = useContext(MappingImportResponseContext);
  const [selectedMappingType, _setSelectedMappingType] = useState(
    allMappingData?.mappingGroundType?.lastSelectedMappingType,
  );
  const [open, setOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isModalCreateOpen, setModalCreateOpen] = useState(false);
  const [sortModel, setSortModel] = useState([
    {
      field: 'externalName',
      sort: 'asc',
    },
  ]);

  const setSelectedMappingType = (importType) => {
    _setSelectedMappingType(importType);
    setAllMappingData((prev) => ({
      ...prev,
      mappingGroundType: {
        ...prev.mappingGroundType,
        lastSelectedMappingType: importType,
      },
    }));
  };

  useEffect(() => {
    if (groundTypeMappingData === null) {
      fetchAllData();
    }
    return () => {
      sendEvent('cancelHttpRequest');
    };
  }, []);

  useEffect(() => {
    if (selectedMappingType !== null) {
      handleMappingTypeChange(selectedMappingType);
    } else {
      if (mappingTypes?.length > 0) {
        handleMappingTypeChange(mappingTypes[0]);
      } else {
        handleMappingTypeChange(IMPORT_TYPE.geofolia);
      }
    }
  }, [groundTypeMappingData]);

  const fetchAllData = async () => {
    try {
      await Promise.all([
        getGroundTypeMappingDataByTypeAndStructure(IMPORT_TYPE.wiuz, cooperativeId),
        getGroundTypeMappingDataByTypeAndStructure(IMPORT_TYPE.geofolia, cooperativeId),
      ]).then((res) => {
        const wiuzMappingData = res?.[0]?.data?.data || [];
        const geofoliaMappingData = res?.[1]?.data?.data || [];

        const groundTypeMappingData = {
          [IMPORT_TYPE.wiuz]: wiuzMappingData,
          [IMPORT_TYPE.geofolia]: geofoliaMappingData,
        };
        setAllMappingData((prev) => ({
          ...prev,
          mappingGroundType: {
            ...prev.mappingGroundType,
            data: groundTypeMappingData,
          },
        }));
      });
    } catch (error) {
      toast.error(error?.message);
    }
  };

  const handleMappingTypeChange = (importType) => {
    resetRowModesModel();
    setSelectedMappingType(importType);
  };

  const refreshDataAfterNewFileImport = (targetMappingType, newMappingData) => {
    setAllMappingData((prev) => ({
      ...prev,
      mappingGroundType: {
        ...prev.mappingGroundType,
        data: { ...prev.mappingGroundType.data, [targetMappingType]: newMappingData },
      },
    }));
  };

  const IconAndTextButton = (icon, text) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      <Typography variant="body2">{text}</Typography>
    </Box>
  );

  const textButton = (text) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2">{text}</Typography>
    </Box>
  );

  const actions = [
    { icon: textButton(IMPORT_TYPE.geofolia), name: IMPORT_TYPE.geofolia },
    { icon: textButton(IMPORT_TYPE.wiuz), name: IMPORT_TYPE.wiuz },
  ];

  const rows = useMemo(() => {
    if (selectedMappingType === null) {
      return [];
    }
    if (groundTypeMappingData?.[selectedMappingType]?.length === 0) {
      return [];
    }
    const rows =
      groundTypeMappingData?.[selectedMappingType]?.map((mappingData) => {
        return {
          id: mappingData.id,
          groundType: mappingData.groundType?.id || null,
          externalName: mappingData.externalName,
          mappingType: mappingData.mappingType,
        };
      }) || [];

    return rows;
  }, [groundTypeMappingData, selectedMappingType]);

  const { getActions, rowModesModel, setRowModesModel, resetRowModesModel, RenderDeleteDialog } =
    useDataGridMappingActions(
      rows,
      cooperativeId,
      refreshDataAfterNewFileImport,
      groundTypeMappingData,
      'mappingGroundType',
    );

  const columns = useMemo(() => {
    return [
      {
        field: 'externalName',
        headerName: 'Nom type de sol referentiel externe',
        headerClassName: 'cropToPlant-header',
        renderHeader: () => (
          <CustomSortableHeader
            setSortModel={setSortModel}
            fieldName={'externalName'}
            name={t('label.externalName')}
          />
        ),
        cellClassName: 'culture-center-cell',
        sortable: true,
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        minWidth: 140,
        type: 'string',
        editable: true,
        preProcessEditCellProps: (params) => {
          const hasError = !params.props.value || params.props.value.trim().length < 1;
          return { ...params.props, error: hasError };
        },
        renderEditCell: (params) => {
          const hasError = !params.value || params.value.trim().length < 1;

          return (
            <TextField
              value={params.value || ''}
              onChange={(e) => {
                params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: e.target.value,
                });
              }}
              error={hasError}
              helperText={hasError ? t('validator.minChar1') : ''}
              variant="standard"
              autoFocus
            />
          );
        },
      },
      {
        field: 'groundType',
        headerName: 'Nom type de sol referentiel assolia',
        headerClassName: 'cropToPlant-header',
        renderHeader: () => (
          <CustomSortableHeader
            setSortModel={setSortModel}
            fieldName={'groundType'}
            name={t('label.groundType')}
          />
        ),
        cellClassName: 'cropToPlant-cell',
        sortable: true,
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        minWidth: 140,
        type: 'singleSelect',
        valueOptions: groundTypeList,
        valueFormatter: (params) => {
          return groundTypeList.find((groundType) => groundType.value === params).label ?? '—';
        },
        editable: true,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        flex: 1,
        minWidth: 160,
        headerAlign: 'center',
        align: 'center',
        cellClassName: 'actions',
        getActions: getActions,
        renderHeader: () => <CustomHeaderWithoutSort name={t('common.actions')} />,
      },
    ];
  }, [groundTypeList, rowModesModel]);

  const processRowUpdate = useCallback(
    async (updatedRow, previousRow) => {
      const mappingType = updatedRow.mappingType;
      const mappingDataId = updatedRow.id;
      const toUpdate = getOnlyAttributeUpdatedBetweenObjects(previousRow, updatedRow);

      if (Object.keys(toUpdate).length === 0) {
        return previousRow;
      }

      toUpdate.id = mappingDataId;

      try {
        const editedMappingData = await editGroundTypeMappingData(
          mappingType,
          cooperativeId,
          toUpdate,
        );
        const newMappingData = groundTypeMappingData?.[mappingType]?.map((mappingData) => {
          if (mappingData.id === mappingDataId) {
            return editedMappingData?.data?.data;
          }
          return mappingData;
        });

        refreshDataAfterNewFileImport(mappingType, newMappingData);
        toast.success(
          t('success.mappingUpdated', { name: editedMappingData?.data?.data.externalName }),
        );
        return updatedRow;
      } catch (error) {
        toast.error(error?.response?.data?.message ?? displayErrorMessage('ERR_UNEXPECTED'));
        return previousRow;
      }
    },
    [groundTypeMappingData],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SpeedDial
        ariaLabel="SpeedDial controlled open"
        icon={IconAndTextButton(<SpeedDialIcon />, t('button.importNewFile'))}
        FabProps={{
          variant: 'extended',
        }}
        open={open}
        onClick={() => setOpen(!open)}
        direction="right"
        sx={{ zIndex: 1 }}
      >
        {actions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            slotProps={{
              fab: {
                variant: 'extended',
              },
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedMappingType(action.name);
              setIsImportModalOpen(true);
            }}
          />
        ))}
      </SpeedDial>
      {groundTypeMappingImportResponse.hasWarning && (
        <AccordionErrorMappingImport from={'mappingGroundType'} />
      )}
      <CustomCultureDataGrid
        fromMapping={true}
        theme={datagridMappingTheme}
        rows={rows}
        columns={columns}
        sortModel={sortModel}
        rowModesModel={rowModesModel}
        setRowModesModel={setRowModesModel}
        processRowUpdate={processRowUpdate}
        customToolbar={
          <CustomToolBarMappingDatagrid
            handleMappingTypeChange={handleMappingTypeChange}
            selectedMappingType={selectedMappingType}
            setModalCreateOpen={setModalCreateOpen}
            from={'mappingGroundType'}
          />
        }
      />
      {isImportModalOpen && (
        <ImportMappingFileModal
          open={isImportModalOpen}
          setModalOpen={setIsImportModalOpen}
          selectedMappingType={selectedMappingType}
          cooperativeId={cooperativeId}
          setOpenImportButton={setOpen}
          refreshDataAfterNewFileImport={refreshDataAfterNewFileImport}
          from={'mappingGroundType'}
        />
      )}
      {isModalCreateOpen && (
        <AddGroundTypeMappingDataModal
          isModalCreateOpen={isModalCreateOpen}
          setModalCreateOpen={setModalCreateOpen}
          groundTypeList={groundTypeList}
          cooperativeId={cooperativeId}
          mappingTypes={mappingTypes}
          selectedMappingType={selectedMappingType}
          actions={actions}
          refreshDataAfterNewFileImport={refreshDataAfterNewFileImport}
          groundTypeMappingData={groundTypeMappingData}
        />
      )}
      <RenderDeleteDialog />
    </Box>
  );
};
export default MappingGroundType;
